import { Alert, Button, CircularProgress, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { projectsApi, ProjectsApiError } from '../../../api/projectsApi';
import { projectSlice } from '../../../redux/ProjectSlice';
import { RootState } from '../../../redux/Store';
import { formatCodeInput, isValidPin } from '../../../utils/codeValidation';
import { isBrowserOffline } from '../../../utils/networkStatus';

interface EnterCollaboratorPinDialogProps {
  onClose: () => void;
  open: boolean;
}

const EnterCollaboratorPinDialog = ({ onClose }: EnterCollaboratorPinDialogProps) => {
  const [collaboratorPin, setCollaboratorPin] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Get the current loaded project and projects array
  const loadedProject = useSelector((state: RootState) => state.project.loadedProject);
  const projects = useSelector((state: RootState) => state.project.projects);

  const handleInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const formatted = formatCodeInput(event.target.value);
    setCollaboratorPin(formatted);
    setError(''); // Clear error when user types
  };

  const handleCancelCollaboratorPin = () => {
    setCollaboratorPin('');
    setError('');
    onClose();
  };

  const handleCollaboratorPinEntered = async () => {
    // Validate PIN format
    if (!isValidPin(collaboratorPin)) {
      setError(t('enterCollaboratorPinDialog.error.invalid-format', 'Collaborator PIN must be 4 uppercase letters and numbers (e.g., AB12)'));
      return;
    }

    if (!loadedProject || !loadedProject.shareCode) {
      setError(t('enterCollaboratorPinDialog.error.no-project', 'No project loaded'));
      return;
    }

    if (isBrowserOffline()) {
      setError(t('enterCollaboratorPinDialog.error.offline', 'You appear to be offline. Connect to the internet to validate the collaborator PIN.'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Validate PIN using dedicated validation endpoint
      await projectsApi.validatePin(loadedProject.shareCode, collaboratorPin);

      // If we got here, the PIN is valid!
      // Update project state
      dispatch(
        projectSlice.actions.onSetProjectPin({
          projectId: loadedProject.id,
          pin: collaboratorPin,
        })
      );

      dispatch(
        projectSlice.actions.onSetAccessLevel({
          projectId: loadedProject.id,
          level: 'collaborator',
        })
      );

      // TODO: Connect to Yjs for collaborative editing
      // This will be implemented when Yjs WebSocket server is set up
      // For now, just enable editing by setting readOnly to false
      dispatch(projectSlice.actions.onReadOnly(false));

      // Save project to local storage (IndexedDB via redux-persist)
      // Check if project already exists in projects array
      const existingProject = projects.find((p) => p.id === loadedProject.id || p.shareCode === loadedProject.shareCode);

      if (!existingProject) {
        // Project doesn't exist locally - add it
        // Create updated project with collaborator access
        const projectToSave = {
          ...loadedProject,
          pin: collaboratorPin,
          accessLevel: 'collaborator' as const,
          readOnly: false,
        };

        dispatch(projectSlice.actions.onAddProject(projectToSave));
      }
      // If project already exists, the onSetProjectPin and onSetAccessLevel actions
      // will have already updated it in the projects array

      // Close dialog
      onClose();
    } catch (err: unknown) {
      console.error('Failed to validate collaborator PIN:', err);

      const error = err as ProjectsApiError;
      if (error.status === 401) {
        setError(t('enterCollaboratorPinDialog.error.invalid-pin', 'Invalid collaborator PIN. Please try again.'));
      } else if (error.status === 404) {
        setError(t('enterCollaboratorPinDialog.error.project-not-found', 'Project not found. It may have been removed.'));
      } else {
        setError(t('enterCollaboratorPinDialog.error.general', 'Failed to verify collaborator PIN. Please check your connection and try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle textAlign="center">
        <Trans i18nKey={'enterCollaboratorPinDialog.title'}>Enter Collaborator PIN</Trans>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} marginTop={1} alignItems={'center'}>
          <Typography>
            <Trans i18nKey={'enterCollaboratorPinDialog.collaborator-pin-explanation'}>
              If you have a collaborator PIN, enter it below to edit the project:
            </Trans>
          </Typography>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <TextField
              id="collaborator-pin"
              placeholder={t('enterCollaboratorPinDialog.input.placeholder', 'AB12')}
              value={collaboratorPin}
              onChange={handleInput}
              disabled={isLoading}
              slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{ maxLength: 4 }}
              error={!!error}
            />
          </Stack>

          {/* Show error message */}
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" display="flex" justifyContent="space-between" width={'100%'}>
            <Button onClick={handleCancelCollaboratorPin} disabled={isLoading}>
              <Trans i18nKey="enterCollaboratorPinDialog.button.cancel">Cancel</Trans>
            </Button>

            <Button
              onClick={handleCollaboratorPinEntered}
              disabled={isLoading || !collaboratorPin}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? (
                <Trans i18nKey="enterCollaboratorPinDialog.button.verifying">Verifying...</Trans>
              ) : (
                <Trans i18nKey="enterCollaboratorPinDialog.button.confirm">Confirm</Trans>
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};

export default EnterCollaboratorPinDialog;
